import { useRef, useState, useEffect, useMemo, Suspense } from 'react';
import { Canvas, useLoader, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid, Center, Html, useProgress } from '@react-three/drei';
import * as THREE from 'three';
import SceneEnvironment from './SceneEnvironment';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';

// Loading progress indicator
function Loader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div className="flex flex-col items-center space-y-2">
        <div className="w-16 h-16 border-4 border-[var(--primary-bg)] border-t-[var(--primary)] rounded-full animate-spin"></div>
        <span className="text-sm font-medium text-[var(--text-secondary)]">{progress.toFixed(0)}% loaded</span>
      </div>
    </Html>
  );
}

// Error boundary for 3D content
function ErrorFallback({ error }) {
  return (
    <Html center>
      <div className="bg-[var(--error-container)] rounded-lg p-4 max-w-xs text-center">
        <div className="text-[var(--error)] font-medium mb-2">Failed to load model</div>
        <div className="text-sm text-[var(--error)]">{error?.message || 'Unknown error'}</div>
      </div>
    </Html>
  );
}

// GLTF/GLB Model component
function GLTFModel({ url, scale = 1, position = [0, 0, 0], rotation = [0, 0, 0], onLoad }) {
  const gltf = useLoader(GLTFLoader, url);

  useEffect(() => {
    if (gltf && onLoad) {
      onLoad(gltf);
    }
  }, [gltf, onLoad]);

  return (
    <Center>
      <primitive
        object={gltf.scene.clone()}
        scale={scale}
        position={position}
        rotation={rotation}
      />
    </Center>
  );
}

// OBJ Model component
function OBJModel({ url, scale = 1, position = [0, 0, 0], rotation = [0, 0, 0], onLoad }) {
  const obj = useLoader(OBJLoader, url);

  useEffect(() => {
    if (obj && onLoad) {
      onLoad(obj);
    }
  }, [obj, onLoad]);

  // Clone and apply default material if needed
  const clonedObj = useMemo(() => {
    const clone = obj.clone();
    clone.traverse((child) => {
      if (child.isMesh && !child.material) {
        child.material = new THREE.MeshStandardMaterial({ color: 0x888888 });
      }
    });
    return clone;
  }, [obj]);

  return (
    <Center>
      <primitive
        object={clonedObj}
        scale={scale}
        position={position}
        rotation={rotation}
      />
    </Center>
  );
}

// FBX Model component
function FBXModel({ url, scale = 1, position = [0, 0, 0], rotation = [0, 0, 0], onLoad }) {
  const fbx = useLoader(FBXLoader, url);

  useEffect(() => {
    if (fbx && onLoad) {
      onLoad(fbx);
    }
  }, [fbx, onLoad]);

  const clonedFbx = useMemo(() => fbx.clone(), [fbx]);

  return (
    <Center>
      <primitive
        object={clonedFbx}
        scale={scale * 0.01} // FBX often needs scaling down
        position={position}
        rotation={rotation}
      />
    </Center>
  );
}

// PLY Point Cloud component
function PLYModel({ url, scale = 1, position = [0, 0, 0], rotation = [0, 0, 0], onLoad }) {
  const geometry = useLoader(PLYLoader, url);

  useEffect(() => {
    if (geometry && onLoad) {
      onLoad(geometry);
    }
  }, [geometry, onLoad]);

  const pointsGeometry = useMemo(() => {
    const geo = geometry.clone();
    geo.computeVertexNormals();
    return geo;
  }, [geometry]);

  // Check if geometry has colors
  const hasColors = pointsGeometry.attributes.color !== undefined;

  return (
    <Center>
      <points scale={scale} position={position} rotation={rotation}>
        <bufferGeometry attach="geometry" {...pointsGeometry} />
        <pointsMaterial
          attach="material"
          size={0.01}
          vertexColors={hasColors}
          color={hasColors ? undefined : 0x888888}
        />
      </points>
    </Center>
  );
}

// Splat/Gaussian Splat placeholder
// Full Gaussian splatting requires specialized WebGL shaders not available in drei
// We show a placeholder instead of fetching the file (which would waste bandwidth)
function SplatModel({ url: _url, scale = 1, position = [0, 0, 0], rotation = [0, 0, 0] }) {
  // NOTE: We intentionally do NOT fetch the file here.
  // Fetching a 50MB+ splat file just to show a placeholder wastes bandwidth.
  // Users can download the file to view in specialized tools like SuperSplat.

  return (
    <Center>
      <mesh scale={scale} position={position} rotation={rotation}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial color="#60a5fa" wireframe />
      </mesh>
      <Html position={[0, 2, 0]} center>
        <div className="bg-[var(--tertiary-fixed)] border-2 border-[var(--tertiary-container)] rounded-lg p-4 max-w-xs text-center">
          <h3 className="font-bold text-[var(--text-main)] mb-2">Gaussian Splat Format</h3>
          <p className="text-sm text-[var(--text-secondary)] mb-3">
            This format requires specialized WebGL shaders for rendering.
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            Use the download button to view in external tools like{' '}
            <a
              href="https://playcanvas.com/supersplat/editor"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--primary)]"
            >
              SuperSplat
            </a>
          </p>
        </div>
      </Html>
    </Center>
  );
}

// USDZ is primarily for iOS AR - show preview mesh
// We intentionally do NOT fetch the file - it's meant for iOS AR Quick Look
function USDZModel({ url: _url, scale = 1, position = [0, 0, 0] }) {
  return (
    <Center>
      <mesh scale={scale} position={position}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#a855f7" />
      </mesh>
      <Html position={[0, 1.5, 0]} center>
        <div className="bg-[var(--secondary-container)] border-2 border-[var(--secondary-container-text)] rounded-lg p-4 max-w-xs text-center">
          <h3 className="font-bold text-[var(--secondary-container-text)] mb-2">USDZ Format</h3>
          <p className="text-sm text-[var(--text-secondary)] mb-3">
            This format is designed for iOS AR Quick Look.
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            Download and open on an iOS device for the best AR experience.
          </p>
        </div>
      </Html>
    </Center>
  );
}

// Auto-rotate camera option — kept for future toggle, not currently wired.
// eslint-disable-next-line no-unused-vars
function AutoRotate({ enabled, speed = 0.5 }) {
  const { camera } = useThree();
  const angle = useRef(0);

  useFrame((_, delta) => {
    if (!enabled) return;
    angle.current += delta * speed;
    const radius = 5;
    camera.position.x = Math.sin(angle.current) * radius;
    camera.position.z = Math.cos(angle.current) * radius;
    camera.lookAt(0, 0, 0);
  });

  return null;
}

// Model selector based on format
function ModelContent({ url, format, scale, position, rotation, onLoad }) {
  switch (format?.toLowerCase()) {
    case 'glb':
    case 'gltf':
      return <GLTFModel url={url} scale={scale} position={position} rotation={rotation} onLoad={onLoad} />;
    case 'obj':
      return <OBJModel url={url} scale={scale} position={position} rotation={rotation} onLoad={onLoad} />;
    case 'fbx':
      return <FBXModel url={url} scale={scale} position={position} rotation={rotation} onLoad={onLoad} />;
    case 'ply':
      return <PLYModel url={url} scale={scale} position={position} rotation={rotation} onLoad={onLoad} />;
    case 'splat':
      return <SplatModel url={url} scale={scale} position={position} rotation={rotation} />;
    case 'usdz':
      return <USDZModel url={url} scale={scale} position={position} />;
    default:
      return (
        <Html center>
          <div className="text-[var(--text-muted)]">Unsupported format: {format}</div>
        </Html>
      );
  }
}

// Main viewer component
const Model3DViewer = ({
  url,
  format,
  className = '',
  scale = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  showGrid = true,
  showControls = true,
  autoRotate = false,
  // Default resolves to a theme-aware surface tone via CSS variable.
  // The Three.js Canvas's `style` is HTML CSS, so var() works here.
  backgroundColor = 'var(--bg-elevated)',
  onModelLoad,
  onError
}) => {
  const [error, setError] = useState(null);
  const [viewSettings, setViewSettings] = useState({
    wireframe: false,
    showGrid: showGrid,
    autoRotate: autoRotate
  });

  const handleLoad = (model) => {
    if (onModelLoad) {
      onModelLoad(model);
    }
  };

  const handleError = (err) => {
    setError(err);
    if (onError) {
      onError(err);
    }
  };

  if (!url) {
    return (
      <div className={`${className} flex items-center justify-center bg-[var(--bg-elevated)] rounded-xl`}>
        <div className="text-[var(--text-muted)] text-center p-8">
          <div className="text-4xl mb-2">📦</div>
          <div>No model selected</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className} bg-[var(--bg-elevated)] rounded-xl overflow-hidden shadow-[var(--shadow-sm)]`}>
      {/* Controls Overlay */}
      {showControls && (
        <div className="absolute top-4 left-4 z-10 flex flex-col space-y-2">
          <div className="bg-glass backdrop-blur rounded-lg shadow-[var(--shadow-sm)] p-2 flex flex-col space-y-1">
            <button
              onClick={() => setViewSettings(s => ({ ...s, showGrid: !s.showGrid }))}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                viewSettings.showGrid
                  ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewSettings(s => ({ ...s, autoRotate: !s.autoRotate }))}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                viewSettings.autoRotate
                  ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              Auto-Rotate
            </button>
          </div>
        </div>
      )}

      {/* Format Badge */}
      <div className="absolute top-4 right-4 z-10">
        <span className="bg-glass backdrop-blur px-3 py-1 rounded-full text-sm font-medium text-[var(--text-secondary)]">
          {format?.toUpperCase() || 'Unknown'}
        </span>
      </div>

      {/* Canvas */}
      <Canvas
        shadows
        style={{ background: backgroundColor }}
        camera={{ position: [3, 3, 3], fov: 50 }}
        onError={handleError}
      >
        <Suspense fallback={<Loader />}>
          <PerspectiveCamera makeDefault position={[3, 3, 3]} fov={50} />
          <OrbitControls
            enablePan
            enableZoom
            enableRotate
            autoRotate={viewSettings.autoRotate}
            autoRotateSpeed={1}
          />

          {/* Lighting */}
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
          <directionalLight position={[-10, 10, -5]} intensity={0.5} />

          {/* Environment for reflections. Procedural (three RoomEnvironment)
              — drei's <Environment preset> fetched an HDR from a third-party
              CDN that the production CSP blocks, killing the viewer. */}
          <SceneEnvironment />

          {/* Grid */}
          {viewSettings.showGrid && (
            <Grid
              args={[20, 20]}
              cellColor="#cbd5e1"
              sectionColor="#94a3b8"
              position={[0, -0.01, 0]}
              fadeDistance={25}
            />
          )}

          {/* Model */}
          {error ? (
            <ErrorFallback error={error} />
          ) : (
            <ModelContent
              url={url}
              format={format}
              scale={scale}
              position={position}
              rotation={rotation}
              onLoad={handleLoad}
            />
          )}
        </Suspense>
      </Canvas>

      {/* Instructions */}
      <div className="absolute bottom-4 left-4 z-10 bg-glass-soft backdrop-blur rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)]">
        <span className="font-medium">Controls:</span> Drag to rotate, Scroll to zoom, Right-drag to pan
      </div>
    </div>
  );
};

export default Model3DViewer;
